import { useEffect, useState } from 'react';
import { isSupabaseConfigured, supabase, PRODUCT, type Session } from './supabase';
import type { PracticeContext } from './types';

export type AuthState =
  | { status: 'loading' }
  | { status: 'signed_out' }
  | {
      status: 'signed_in';
      session: Session;
      practiceLoading: boolean;
      context: PracticeContext | null;
    };

export function useAuth(): AuthState {
  const [state, setState] = useState<AuthState>({ status: 'loading' });

  useEffect(() => {
    if (!isSupabaseConfigured) {
      setState({ status: 'signed_out' });
      return;
    }

    let cancelled = false;

    async function probePractice(session: Session) {
      const { data, error } = await supabase.rpc('get_my_practice_context', {
        p_product: PRODUCT,
      });

      if (cancelled) return;

      if (error) {
        console.warn('[web] get_my_practice_context:', error.message);
        setState({
          status: 'signed_in',
          session,
          practiceLoading: false,
          context: null,
        });
        return;
      }

      if (!data?.practice) {
        setState({
          status: 'signed_in',
          session,
          practiceLoading: false,
          context: null,
        });
        return;
      }

      setState({
        status: 'signed_in',
        session,
        practiceLoading: false,
        context: {
          memberRole: data.memberRole as PracticeContext['memberRole'],
          practice: data.practice as PracticeContext['practice'],
        },
      });
    }

    supabase.auth
      .getSession()
      .then(({ data }) => {
        if (cancelled) return;
        if (data.session) {
          setState({
            status: 'signed_in',
            session: data.session,
            practiceLoading: true,
            context: null,
          });
          void probePractice(data.session);
        } else {
          setState({ status: 'signed_out' });
        }
      })
      .catch((err: unknown) => {
        console.warn('[web] getSession failed:', err);
        if (!cancelled) setState({ status: 'signed_out' });
      });

    const { data: sub } = supabase.auth.onAuthStateChange((_event, session) => {
      if (cancelled) return;
      if (session) {
        setState({
          status: 'signed_in',
          session,
          practiceLoading: true,
          context: null,
        });
        void probePractice(session);
      } else {
        setState({ status: 'signed_out' });
      }
    });

    return () => {
      cancelled = true;
      sub.subscription.unsubscribe();
    };
  }, []);

  return state;
}

export async function signOut() {
  await supabase.auth.signOut();
}

export async function sendMagicLink(email: string, redirectTo: string) {
  return await supabase.auth.signInWithOtp({
    email,
    options: {
      emailRedirectTo: redirectTo,
      shouldCreateUser: true,
    },
  });
}
